import DOMPurify from "dompurify";

// What RichTextEditor produces; anything else is dropped when displaying. Images only where asked for (documentation).
const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'a'];
const ALLOWED_ATTR = ['href'];
const IMAGE_ATTR = ['src', 'alt', 'width', 'height'];

// Links to other sites open in a new tab, without giving the other page a handle on this one; links to the site's own
// pages (/docs/glossary#term-lfo) open in place. Images only from https links (DOMPurify would also let data: URLs through).
DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A' && node.hasAttribute('href') && !/^\/(?!\/)/.test(node.getAttribute('href') ?? '')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
    }
    if (node.tagName === 'IMG' && !/^https:\/\//i.test(node.getAttribute('src') ?? '')) {
        node.remove();
    }
});

const escape = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Descriptions written before the rich text editor are plain text: blank lines separate paragraphs.
export function toHtml(value: string): string {
    if (!value.trim() || /<[a-z][^>]*>/i.test(value)) {
        return value;
    }
    return value.trim().split(/\n\s*\n/)
        .map(x => `<p>${escape(x.trim()).replace(/\n/g, '<br>')}</p>`)
        .join('');
}

// images: keep <img> (https links only, as DOMPurify allows no scripts or data: URLs in src by default).
export function sanitizeHtml(value: string, options?: { images?: boolean }): string {
    return options?.images
        ? DOMPurify.sanitize(toHtml(value), { ALLOWED_TAGS: [...ALLOWED_TAGS, 'img'], ALLOWED_ATTR: [...ALLOWED_ATTR, ...IMAGE_ATTR] })
        : DOMPurify.sanitize(toHtml(value), { ALLOWED_TAGS, ALLOWED_ATTR });
}

// For summaries: the text alone, with a space where blocks and line breaks were.
export function htmlToText(value: string): string {
    const spaced = sanitizeHtml(value).replace(/(<\/(?:p|li|h\d|blockquote)>|<br\s*\/?>)/gi, '$1 ');
    const text = new DOMParser().parseFromString(spaced, 'text/html').body.textContent ?? '';
    return text.replace(/\s+/g, ' ').trim();
}

// For cards and meta descriptions: the introduction, before the first heading (all of it if there's none), as
// text. Showing the whole description and hiding most of it would repeat it page after page, as hidden text.
export function htmlSummary(value: string): string {
    const html = toHtml(value);
    const heading = html.search(/<h\d[\s>]/i);
    return htmlToText(heading > 0 ? html.slice(0, heading) : html);
}

// For a showcase's hero: the description's first paragraph, as text (the summary when it doesn't start with one).
export function htmlLead(value: string): string {
    const first = new DOMParser().parseFromString(sanitizeHtml(value), 'text/html').body.firstElementChild;
    return first?.tagName === 'P' ? htmlToText(first.outerHTML) : htmlSummary(value);
}

// Its headings a level lower (h2 to h3, and so on; h6 stays), for HTML shown under a heading of the page's own: the
// product page's description, under "About …".
export function htmlDemoteHeadings(value: string): string {
    return value.replace(/<(\/?)h([1-5])(?=[\s>])/gi, (_, slash: string, level: string) => `<${slash}h${Number(level) + 1}`);
}

// The rest of the description, under a hero that shows htmlLead (sanitized, so RichText can show it as it is): without
// its first paragraph, so it isn't said twice.
export function htmlAfterLead(value: string): string {
    const body = new DOMParser().parseFromString(sanitizeHtml(value), 'text/html').body;
    if (body.firstElementChild?.tagName === 'P') {
        body.firstElementChild.remove();
    }
    return body.innerHTML.trim();
}
